import { ReactNode, useState } from 'react'
import type { FilterDropdownProps } from 'antd/es/table/interface'
import { Button, Checkbox, Flex, Input } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import Highlighter from 'react-highlight-words'
import { useSearchParams } from 'react-router-dom'

/**
 * Хук для локального поиска и фильтрации строк в колонке Ant Design Table.
 * Предоставляет возможности:
 * - поиска с подсветкой (highlight) при вводе каждого символа
 * - включение/выключение фильтра строк по значению в input
 * - кастомный render для ячеек c вожможностью подсветки элементов
 */

type ParamsSearch<T> = {
  dataIndex: keyof T
  customRender?: (highlightedValue: ReactNode, record: T) => ReactNode
}

export const useColumnLocalSearch = <T extends Record<string, unknown>>({
  dataIndex,
  customRender,
}: ParamsSearch<T>) => {
  const [searchParams] = useSearchParams()
  const initialValue = searchParams.get(`searchField_${String(dataIndex)}`)

  const [searchText, setSearchText] = useState<string>(initialValue ?? '')
  const [isFilter, setIsFilter] = useState(Boolean(initialValue))

  const filterDropdown = ({
    setSelectedKeys,
    confirm,
    clearFilters,
  }: FilterDropdownProps) => {
    return (
      <Flex vertical gap={8} style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${String(dataIndex)}`}
          value={searchText}
          onChange={({ target }) => {
            const { value } = target
            setSearchText(value)

            if (!value) {
              clearFilters?.()
              setIsFilter(false)
              confirm({ closeDropdown: false })
            }
          }}
        />
        <Flex gap={8} justify="space-between">
          <Checkbox
            checked={isFilter}
            onChange={({ target }) => {
              const isChecked = target.checked
              setIsFilter(isChecked)

              isChecked && searchText
                ? setSelectedKeys([searchText])
                : clearFilters?.()

              confirm({ closeDropdown: false })
            }}
          >
            <div style={{ marginTop: 2 }}>Filter</div>
          </Checkbox>
          <Button
            size="small"
            onClick={() => {
              clearFilters?.()
              setSearchText('')
              setIsFilter(false)
              confirm({ closeDropdown: false })
            }}
          >
            Reset
          </Button>
        </Flex>
      </Flex>
    )
  }

  const onFilter = (value: any, record: T) => {
    return String(record[dataIndex])
      .toLowerCase()
      .includes(String(value).toLowerCase())
  }

  const filterIcon = () => (
    <SearchOutlined
      style={{ color: searchText ? 'var(--color-primary)' : undefined }}
    />
  )

  const render = (value: unknown, record: T) => {
    const highlightedValue = (
      <Highlighter
        searchWords={[searchText]}
        highlightStyle={{
          backgroundColor: 'var(--color-yellow-50)',
          padding: 0,
        }}
        autoEscape
        textToHighlight={String(value ?? '')}
      />
    )

    if (customRender) {
      return customRender(highlightedValue, record)
    }

    return highlightedValue
  }

  return {
    filterDropdown,
    onFilter,
    filterIcon,
    render,
    filteredValue: isFilter && searchText ? [searchText] : null,
  }
}


